import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, KeyboardEvent } from 'react';

import type { SlashCommandItem } from '../../hooks/use-slash-commands';
import type { MySkill } from '../../lib/api-client';

import { FileChip } from './FileChip';
import { SlashCommandPicker, filterCommands } from './SlashCommandPicker';
import { SkillSelectorPopover } from './SkillSelectorPopover';

interface Props {
  disabled?: boolean;
  busy?: boolean;
  onSend: (text: string, files?: File[]) => void | Promise<void>;
  onStop?: (() => void) | undefined;
  /** Slash commands — when empty the picker is hidden entirely. */
  slashCommands?: SlashCommandItem[];
  slashStatus?: 'idle' | 'loading' | 'success' | 'error';
  onRefreshSlashCommands?: () => void;
  /** Lazy-load: called once the first time the picker opens (to fetch). */
  onOpenSlashPicker?: () => void;
  /** Per-conversation skill selector — hidden entirely when the user has none. */
  availableSkills?: MySkill[];
  skillsStatus?: 'idle' | 'loading' | 'success' | 'error';
  /** null = unset (all available); array = explicit selection. */
  selectedSkillIds?: string[] | null;
  onToggleSkill?: (skillId: string) => void;
  onSelectAllSkills?: () => void;
  onSelectNoneSkills?: () => void;
  onResetSkills?: () => void;
  /** Persist the selection (called when the popover closes). */
  onSaveSkills?: () => void;
}

/**
 * Imperative handle so parents (e.g. the conversation empty-state playbook
 * suggestions) can type a starter message into the composer without lifting all
 * of its internal state up. `prefill` sets the text, focuses, and moves the
 * caret to the end — it does NOT send.
 */
export interface ComposerHandle {
  prefill: (text: string) => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MAX_FILES = 6;
// Auto-grow ceiling: 5 lines. lineHeight 1.5 × fontSize 13 = 19.5px/line ⇒ ~98px.
const TEXTAREA_LINE_HEIGHT = 1.5;
const TEXTAREA_FONT_SIZE = 13;
const TEXTAREA_MAX_LINES = 5;
const TEXTAREA_MAX_HEIGHT = Math.round(TEXTAREA_LINE_HEIGHT * TEXTAREA_FONT_SIZE * TEXTAREA_MAX_LINES);

export const Composer = forwardRef<ComposerHandle, Props>(function Composer({
  disabled = false,
  busy = false,
  onSend,
  onStop,
  slashCommands = [],
  slashStatus = 'idle',
  onRefreshSlashCommands,
  onOpenSlashPicker,
  availableSkills = [],
  skillsStatus = 'idle',
  selectedSkillIds = null,
  onToggleSkill,
  onSelectAllSkills,
  onSelectNoneSkills,
  onResetSkills,
  onSaveSkills,
}: Props, ref) {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const slashLoadedRef = useRef(false);

  // Auto-grow the textarea up to TEXTAREA_MAX_HEIGHT (5 lines), then scroll.
  // Without this, rows={1} clips multi-line input out of view.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  const skillsNarrowed = selectedSkillIds !== null;
  const skillsBadge = skillsNarrowed
    ? `${selectedSkillIds!.length}/${availableSkills.length}`
    : null;
  const closeSkills = useCallback(() => {
    setSkillsOpen(false);
    onSaveSkills?.();
  }, [onSaveSkills]);

  const slashFilter = useMemo(() => {
    if (!slashOpen) return '';
    const first = value.split('\n')[0] ?? '';
    const m = first.match(/^\/(\S*)/);
    return m ? m[1] ?? '' : '';
  }, [slashOpen, value]);

  const filteredSlash = useMemo(
    () => (slashOpen ? filterCommands(slashCommands, slashFilter) : []),
    [slashOpen, slashCommands, slashFilter],
  );

  const openSlash = useCallback(() => {
    if (!slashOpen) {
      setSlashOpen(true);
      setSlashIndex(0);
      if (!slashLoadedRef.current) {
        slashLoadedRef.current = true;
        onOpenSlashPicker?.();
      }
    }
  }, [slashOpen, onOpenSlashPicker]);

  const closeSlash = useCallback(() => setSlashOpen(false), []);

  const selectSlashCommand = useCallback(
    (cmd: SlashCommandItem) => {
      const next = `/${cmd.name} `;
      setValue(next);
      setSlashOpen(false);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    },
    [],
  );

  // Let parents type a starter message into the prompt (see ComposerHandle).
  useImperativeHandle(
    ref,
    () => ({
      prefill: (text: string) => {
        setValue(text);
        setSlashOpen(false);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
          }
        });
      },
    }),
    [],
  );

  const addFiles = useCallback((incoming: File[]) => {
    setFileError(null);
    setFiles((prev) => {
      const combined = [...prev];
      for (const f of incoming) {
        if (combined.length >= MAX_FILES) {
          setFileError(`Maximum ${MAX_FILES} attachments`);
          break;
        }
        if (f.size > MAX_FILE_SIZE) {
          setFileError(`${f.name} is larger than 25MB`);
          continue;
        }
        if (combined.some((x) => x.name === f.name && x.size === f.size)) continue;
        combined.push(f);
      }
      return combined;
    });
  }, []);

  const removeFile = useCallback((idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    addFiles(Array.from(list));
    e.target.value = ''; // allow same file again later
  };

  const submit = useCallback(async () => {
    const text = value.trim();
    if ((!text && files.length === 0) || disabled) return;
    setValue('');
    setFiles([]);
    setSlashOpen(false);
    setFileError(null);
    await onSend(text, files.length > 0 ? files : undefined);
  }, [value, files, onSend, disabled]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setValue(next);
    const firstLine = next.split('\n')[0] ?? '';
    const atStart = firstLine.startsWith('/');
    if (atStart && !slashOpen) openSlash();
    else if (!atStart && slashOpen) closeSlash();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashOpen && filteredSlash.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((i) => Math.min(filteredSlash.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const pick = filteredSlash[slashIndex];
        if (pick) selectSlashCommand(pick);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeSlash();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      void submit();
    }
  };

  const handleDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const list = e.dataTransfer.files;
    if (list && list.length > 0) addFiles(Array.from(list));
  };

  const sendDisabled = disabled || (!value.trim() && files.length === 0);

  return (
    <div
      style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg)',
        position: 'relative',
      }}
    >
      {fileError && (
        <div
          role="alert"
          style={{
            marginBottom: 8,
            fontSize: 11,
            color: 'var(--kind-failure)',
            padding: '4px 8px',
            background: 'rgba(192,57,43,0.06)',
            border: '1px solid rgba(192,57,43,0.18)',
            borderRadius: 4,
          }}
        >
          {fileError}
        </div>
      )}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {files.map((f, i) => (
            <FileChip
              key={`${f.name}-${i}`}
              file={f}
              status="idle"
              progress={0}
              onRemove={() => removeFile(i)}
            />
          ))}
        </div>
      )}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          background: 'var(--surface)',
          border: `1px solid ${isDragging ? 'var(--accent)' : 'var(--border-strong)'}`,
          borderRadius: 10,
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          position: 'relative',
          transition: 'border-color 120ms ease',
        }}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          title="Attach files"
          aria-label="Attach files"
          style={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 0,
            color: 'var(--text-soft)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 16,
            padding: 0,
            flexShrink: 0,
          }}
        >
          📎
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        {availableSkills.length > 0 && (
          <button
            type="button"
            onClick={() => (skillsOpen ? closeSkills() : setSkillsOpen(true))}
            disabled={disabled}
            title="Choose which skills this conversation can use"
            aria-label="Choose skills"
            aria-expanded={skillsOpen}
            style={{
              position: 'relative',
              height: 28,
              minWidth: 28,
              padding: skillsBadge ? '0 6px' : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              background: skillsNarrowed ? 'var(--accent-soft)' : 'transparent',
              border: skillsNarrowed ? '1px solid var(--accent)' : '1px solid transparent',
              borderRadius: 6,
              color: 'var(--text-soft)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            🧩
            {skillsBadge && (
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)' }}>{skillsBadge}</span>
            )}
          </button>
        )}
        {skillsOpen && (
          <SkillSelectorPopover
            available={availableSkills}
            status={skillsStatus}
            selected={selectedSkillIds}
            onToggle={(id) => onToggleSkill?.(id)}
            onSelectAll={() => onSelectAllSkills?.()}
            onSelectNone={() => onSelectNoneSkills?.()}
            onReset={() => onResetSkills?.()}
            onClose={closeSkills}
          />
        )}
        <textarea
          ref={textareaRef}
          placeholder={slashCommands.length > 0 ? 'Reply to agent… (type / for commands)' : 'Reply to agent…'}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            fontSize: TEXTAREA_FONT_SIZE,
            fontFamily: 'inherit',
            background: 'transparent',
            color: 'var(--text)',
            resize: 'none',
            minHeight: 20,
            // Auto-grow effect sets height up to this ceiling, then scrolls.
            maxHeight: TEXTAREA_MAX_HEIGHT,
            lineHeight: TEXTAREA_LINE_HEIGHT,
          }}
        />
        {busy && onStop ? (
          <button
            type="button"
            onClick={onStop}
            style={{
              padding: '6px 12px',
              background: 'var(--bg-alt2)',
              color: 'var(--text)',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void submit()}
            disabled={sendDisabled}
            style={{
              padding: '6px 12px',
              background: 'var(--accent)',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              fontSize: 12,
              cursor: sendDisabled ? 'not-allowed' : 'pointer',
              opacity: sendDisabled ? 0.6 : 1,
              fontWeight: 500,
            }}
          >
            Send
          </button>
        )}
        {slashOpen && (
          <SlashCommandPicker
            commands={slashCommands}
            filter={slashFilter}
            status={slashStatus}
            selectedIndex={Math.min(slashIndex, Math.max(0, filteredSlash.length - 1))}
            onHover={setSlashIndex}
            onSelect={selectSlashCommand}
            onClose={closeSlash}
            onRefresh={onRefreshSlashCommands}
          />
        )}
      </div>
    </div>
  );
});
